import { useEffect, useState } from 'react';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import { Button } from '@/components/ui/therapist/Button';
import { Card } from '@/components/ui/therapist/Card';
import { copySet } from '@/api/services/user';
import { Divider } from 'react-native-elements';
import { logError } from '@/utils/logHandling';
import { PatientSelect } from '@/components/ui/therapist/PatientSelect';
import { Pill } from '@/components/Pill';
import { StackScreenProps } from '@react-navigation/stack';
import { Text } from '@/components/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useSubmitState } from '@/hooks/useSubmitState';
import { View } from 'react-native';

import { styles } from './styles';
import colors from '@/config/colors';
import theme from '@/config/theme';

const SET_API_NAME_MAP = {
  Wörtersets: {
    name: 'wordsets',
    icon: 'letter',
    type: 'wordset',
  },
  Sätzesets: {
    name: 'sentencesets',
    icon: 'sentence',
    type: 'sentenceset',
  },
  Textesets: {
    name: 'textsets',
    icon: 'book',
    type: 'textset',
  },
} as const;

export const CopySet = ({
  route,
  navigation,
}: StackScreenProps<TherapistStackParamList, 'CopySet'>) => {
  const [patientIds, setPatientIds] = useState<string[]>([]);
  const { showNotification } = useNotification();
  const { setCopyFromPatientId, setId, setName, setType } = route?.params;
  const config = SET_API_NAME_MAP[setType];

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button label="Abbrechen" icon="close" onPress={navigation.goBack} />
      ),
    });
  }, []);

  const copySets = async () => {
    try {
      await copySet({
        type: config.type,
        patientIds,
        sourceSetId: setId,
      });
      showNotification({
        type: 'success',
        title: `Das Set wurde ${patientIds.length > 1 ? `${patientIds.length} weiteren Patient:innen` : '1 Patient:in'} zugewiesen.`,
      });
      navigation.goBack();
    } catch (error) {
      showNotification({
        type: 'error',
        title:
          'Das Set konnte nicht kopiert werden. Bitte versuchen Sie es erneut.',
      });
      logError('Copying a set error', { error });
    }
  };

  const { isSubmitting, submit } = useSubmitState({ onSubmit: copySets });

  return (
    <BodyWrapper showSidebar={false} bodyStyle={{ width: '80%', flex: 1 }}>
      <Card bottomDent title="Set zuweisen" styleBody={{ padding: 0 }}>
        <View style={styles.titleBox}>
          <Text>
            Bitte wählen Sie die Patient:innen aus, denen Sie das Set zuweisen
            möchten. Das Set erscheint anschließend auf den entsprechenden
            Patienten-Profilen.
          </Text>
        </View>
        <Divider width={2} color={colors.lightGrey} />
        <View style={styles.setNameBox}>
          <View style={styles.boxWrapper}>
            <Pill
              label={''}
              icon={config.icon}
              isNeolexon={false}
              iconSize={22}
            />
            <View style={{ paddingLeft: theme.spacings.lg }}>
              <Text style={{ fontWeight: 'bold' }}>{setName}</Text>
            </View>
          </View>
        </View>
      </Card>

      <PatientSelect
        alreadySelectedPatientIdsToDisable={
          setCopyFromPatientId ? [setCopyFromPatientId] : []
        }
        patientIds={patientIds}
        setPatientIds={setPatientIds}
      />

      <View style={styles.buttonWrapper}>
        <Button look="tertiary" label="Abbrechen" onPress={navigation.goBack} />
        <Button
          disabled={!patientIds.length || isSubmitting}
          label={`Set ${patientIds.length} ${patientIds.length > 1 || patientIds.length === 0 ? 'Patient:innen' : 'Patient:in'} zuweisen`}
          icon="check"
          onPress={submit}
        />
      </View>
    </BodyWrapper>
  );
};
